import { DEFAULT_RELAY_WIRING, RelayWiringMap } from "@app/shared/src/helpers/relayWiring";
import { ClientEndpoint, EventTrigger, FleetDefaults, RelaySelection } from "@app/shared/src/models";
import { AlertCategory } from "./alertCategories";

/**
 * Who hears about an alert, and what fires when it does.
 *
 * Both live on the trigger's endpoint joins rather than on the trigger: an email is sent because a
 * row links the trigger to an email endpoint, and a relay closes because a row links it to a web
 * relay whose config names the terminal. So "notify on speeding" is the presence or absence of a
 * join, not a column.
 *
 * The relay config is a firmware contract. Devices download and parse this JSON to decide what to
 * close, so the shape written here has to stay something an older unit can still read — see the
 * warning on WebRelayEndpointConfig. In practice that means: keep the `$type` discriminators the
 * server round-trips, always write `Expression` (it is [Required], and it must EVALUATE — see
 * RELAY_FIRE_EXPRESSION), and leave `PulseTime` absent rather than inventing one, because it is
 * optional and most rows omit it.
 */

/**
 * The expression a relay entry needs before the device will close the terminal at all.
 *
 * The firmware evaluates each entry's Expression against the event and fires only when it comes
 * back true — and it skips an entry whose expression is blank outright (WebRelayClientEndpointHandler:
 * "a relay with no expression has nothing to evaluate"). This console used to write "" because
 * that is what the rows on the dashboard carried, which meant every truck it wired had beacons and
 * a horn that never fired. "true" is the dashboard's own convention for "fire on every event of
 * this trigger"; anything more specific is an operator's hand-written filter and is kept as is.
 */
export const RELAY_FIRE_EXPRESSION = "true";

const expressionOf = (e: RelayEntry): string | null | undefined => e.expression ?? e.Expression;
const isBlankExpression = (e: RelayEntry): boolean => !(expressionOf(e) ?? "").trim();

/** Whether any relay entry on the trigger has an expression the device will never evaluate. */
export function hasBlankRelayExpressions(trigger: EventTrigger | undefined): boolean {
    for (const join of trigger?.eventTriggerClientEndpoints ?? []) {
        if (!isRelayEndpoint(join.clientEndpoint as { $type?: string } | undefined)) continue;
        if (entriesOf(parseBlob(join.endpointConfig)).some(isBlankExpression)) return true;
    }
    return false;
}

/**
 * Give every blank relay expression the fire-always expression, in place. Returns whether anything
 * changed. A hand-written expression is not touched: it is a filter someone meant.
 */
export function repairRelayExpressions(trigger: EventTrigger): boolean {
    let changed = false;
    for (const join of trigger.eventTriggerClientEndpoints ?? []) {
        if (!isRelayEndpoint(join.clientEndpoint as { $type?: string } | undefined)) continue;
        const blob = parseBlob(join.endpointConfig);
        if (!blob) continue;
        let joinChanged = false;
        for (const entry of entriesOf(blob)) {
            if (!isBlankExpression(entry)) continue;
            if (entry.Expression !== undefined) entry.Expression = RELAY_FIRE_EXPRESSION;
            else entry.expression = RELAY_FIRE_EXPRESSION;
            joinChanged = true;
        }
        if (joinChanged) {
            join.endpointConfig = JSON.stringify(blob);
            changed = true;
        }
    }
    return changed;
}

/**
 * The standard build's terminals, used only as the default wiring. Which relay is actually the
 * horn and which the lights is configurable per fleet and per truck now — pass the effective
 * wiring (see @app/shared relayWiring) to the helpers below wherever a real truck is in hand.
 */
export const LIGHTS_RELAY_NUMBER = DEFAULT_RELAY_WIRING.lights.number;
export const HORN_RELAY_NUMBER = DEFAULT_RELAY_WIRING.horn.number;

const WEB_RELAY_ENDPOINT_CONFIG_TYPE = "Shared.Models.Api.V1.ClientEndpoints.WebRelayEndpointConfig, ";
const WEB_RELAY_CONFIG_TYPE = "Shared.Models.Api.V1.ClientEndpoints.WebRelayConfig, ";

/** WebRelayState.PulseOrReboot — close the relay briefly rather than latch it on. */
const PULSE_OR_REBOOT = 2;

type RelayEntry = {
    $type?: string;
    relay?: number;
    Relay?: number;
    expression?: string | null;
    Expression?: string | null;
    stateToSend?: number;
    StateToSend?: number;
    pulseTime?: number | null;
    PulseTime?: number | null;
};

type RelayConfigBlob = { $type?: string; relays?: RelayEntry[]; Relays?: RelayEntry[] };

export const isEmailEndpoint = (e: { $type?: string } | undefined): boolean => Boolean(e?.$type?.includes("Email"));
export const isRelayEndpoint = (e: { $type?: string } | undefined): boolean => Boolean(e?.$type?.includes("WebRelay"));

/**
 * This truck's relay endpoint out of everything the facility route returned.
 *
 * The route also hands back org-wide endpoints (facilityId null), so the truck's own relay is
 * preferred and an org-wide one is only a fallback — otherwise which relay gets reported depends on
 * the order Postgres happened to return rows in. Shared with AlertsRelaysSection so the relay whose
 * health is reported here is the same one that section edits.
 */
export const findRelayEndpoint = (endpoints: ClientEndpoint[] | undefined, facilityId?: string): ClientEndpoint | undefined => {
    const relays = (endpoints ?? []).filter((e) => isRelayEndpoint(e as { $type?: string }));
    return relays.find((e) => !!facilityId && e.facilityId === facilityId) ?? relays[0];
};

function parseBlob(raw: string | undefined | null): RelayConfigBlob | undefined {
    if (!raw) return undefined;
    try {
        return JSON.parse(raw) as RelayConfigBlob;
    } catch {
        // Device-authored and unreadable: leave it be rather than overwrite what we cannot interpret.
        return undefined;
    }
}

const entriesOf = (blob: RelayConfigBlob | undefined): RelayEntry[] => blob?.relays ?? blob?.Relays ?? [];
const relayNumberOf = (e: RelayEntry): number | undefined => e.relay ?? e.Relay;

/** Which slots this trigger currently closes, across every relay endpoint joined to it. */
export function relaySelectionOf(
    trigger: EventTrigger | undefined,
    wiring: RelayWiringMap = DEFAULT_RELAY_WIRING,
): RelaySelection {
    let selection: RelaySelection = 0 as RelaySelection;
    for (const join of trigger?.eventTriggerClientEndpoints ?? []) {
        if (!isRelayEndpoint(join.clientEndpoint as { $type?: string } | undefined)) continue;
        for (const entry of entriesOf(parseBlob(join.endpointConfig))) {
            const n = relayNumberOf(entry);
            if (n === wiring.lights.number) selection = (selection | 1) as RelaySelection;
            if (n === wiring.horn.number) selection = (selection | 2) as RelaySelection;
        }
    }
    return selection;
}

/** Whether this trigger emails anyone. */
export function notifiesByEmail(trigger: EventTrigger | undefined): boolean {
    return (trigger?.eventTriggerClientEndpoints ?? []).some((j) =>
        isEmailEndpoint(j.clientEndpoint as { $type?: string } | undefined),
    );
}

const wantsRelay = (selection: RelaySelection, relayNumber: number, wiring: RelayWiringMap) =>
    relayNumber === wiring.lights.number ? (selection & 1) !== 0 : (selection & 2) !== 0;

/**
 * Rewrite a relay join's config to close exactly the terminals in `selection`.
 *
 * Existing entries are edited in place rather than rebuilt, so an operator's expression, state and
 * pulse survive a change that only meant to add or drop a terminal. Returns whether anything moved,
 * so a caller can skip a save that would change nothing.
 *
 * `endpoint` is the site's relay board, needed only when the trigger has no relay join yet. Without
 * it this function could only ever edit joins that already existed, so asking for relays on a
 * freshly provisioned trigger did nothing at all and the truck stayed silent — no beacons, no horn,
 * no error. Pass it wherever a trigger might be new; omitting it keeps the old edit-only behaviour
 * for callers that know the join is there.
 */
export function applyRelaySelection(
    trigger: EventTrigger,
    selection: RelaySelection,
    endpoint?: ClientEndpoint,
    wiring: RelayWiringMap = DEFAULT_RELAY_WIRING,
): boolean {
    let changed = false;

    const joins = trigger.eventTriggerClientEndpoints ?? [];
    const hasRelayJoin = joins.some((j) => isRelayEndpoint(j.clientEndpoint as { $type?: string } | undefined));

    // Nothing to write into. Create the join so the loop below has a config to fill; an empty
    // selection is left alone, because a join that closes no terminal is just noise on the trigger.
    if (!hasRelayJoin && endpoint && selection !== 0) {
        trigger.eventTriggerClientEndpoints = [
            ...joins,
            {
                eventTriggerId: trigger.id,
                clientEndpointId: endpoint.id,
                clientEndpoint: endpoint,
                endpointConfig: JSON.stringify({ $type: WEB_RELAY_ENDPOINT_CONFIG_TYPE, relays: [] }),
            },
        ] as typeof trigger.eventTriggerClientEndpoints;
        changed = true;
    }

    for (const join of trigger.eventTriggerClientEndpoints ?? []) {
        if (!isRelayEndpoint(join.clientEndpoint as { $type?: string } | undefined)) continue;

        const blob = parseBlob(join.endpointConfig) ?? { $type: WEB_RELAY_ENDPOINT_CONFIG_TYPE, relays: [] };
        const usesUpperCase = blob.Relays !== undefined;
        const entries = [...entriesOf(blob)];

        const kept = entries.filter((e) => {
            const n = relayNumberOf(e);
            return n !== undefined && wantsRelay(selection, n, wiring);
        });

        // An entry that survives still has to fire: earlier builds of this console wrote blank
        // expressions, which the device skips. Repaired here so a save that only meant to add or
        // drop a terminal also brings the truck's existing entries to life.
        for (const entry of kept) {
            if (!isBlankExpression(entry)) continue;
            if (entry.Expression !== undefined) entry.Expression = RELAY_FIRE_EXPRESSION;
            else entry.expression = RELAY_FIRE_EXPRESSION;
            changed = true;
        }

        for (const relayNumber of [wiring.lights.number, wiring.horn.number]) {
            if (!wantsRelay(selection, relayNumber, wiring)) continue;
            if (kept.some((e) => relayNumberOf(e) === relayNumber)) continue;

            // Expression is [Required] on the contract and must evaluate true for the device to
            // close the terminal — see RELAY_FIRE_EXPRESSION. PulseTime is left off deliberately —
            // it is optional and inventing one would change how long a relay is held closed on a
            // device nobody asked to reconfigure.
            kept.push({
                $type: WEB_RELAY_CONFIG_TYPE,
                relay: relayNumber,
                expression: RELAY_FIRE_EXPRESSION,
                stateToSend: PULSE_OR_REBOOT,
            });
        }

        if (kept.length !== entries.length || kept.some((e, i) => e !== entries[i])) changed = true;

        const next: RelayConfigBlob = { $type: blob.$type ?? WEB_RELAY_ENDPOINT_CONFIG_TYPE };
        if (usesUpperCase) next.Relays = kept;
        else next.relays = kept;

        join.endpointConfig = JSON.stringify(next);
    }

    return changed;
}

/**
 * Move a trigger's relay entries from one wiring to another, role-preserving.
 *
 * The join configs store raw terminal numbers, so changing a truck's wiring (fixing a backwards
 * hookup) must renumber what its triggers already carry — otherwise the alert keeps firing the old
 * terminal and the "fix" changes nothing. Entries are remapped from a snapshot so a straight swap
 * (horn↔lights) cannot cascade, and everything else on each entry — expression, state, pulse —
 * survives untouched. Entries on terminals neither wiring names are left alone: someone configured
 * those by hand for hardware this model does not describe.
 */
export function rewireRelayNumbers(
    trigger: EventTrigger,
    oldWiring: RelayWiringMap,
    newWiring: RelayWiringMap,
): boolean {
    let changed = false;

    for (const join of trigger.eventTriggerClientEndpoints ?? []) {
        if (!isRelayEndpoint(join.clientEndpoint as { $type?: string } | undefined)) continue;

        const blob = parseBlob(join.endpointConfig);
        if (!blob) continue;

        let joinChanged = false;
        for (const entry of entriesOf(blob)) {
            const n = relayNumberOf(entry);
            const target =
                n === oldWiring.horn.number ? newWiring.horn.number
                : n === oldWiring.lights.number ? newWiring.lights.number
                : undefined;
            if (target === undefined || target === n) continue;

            if (entry.Relay !== undefined) entry.Relay = target;
            else entry.relay = target;
            joinChanged = true;
        }

        if (joinChanged) {
            join.endpointConfig = JSON.stringify(blob);
            changed = true;
        }
    }

    return changed;
}

/** Attach or detach an email endpoint, which is what turns email notification on and off. */
export function applyEmailNotification(trigger: EventTrigger, endpoint: ClientEndpoint, notify: boolean): boolean {
    const joins = trigger.eventTriggerClientEndpoints ?? [];
    const has = joins.some((j) => j.clientEndpointId === endpoint.id);
    if (has === notify) return false;

    trigger.eventTriggerClientEndpoints = notify
        ? ([...joins, { eventTriggerId: trigger.id, clientEndpointId: endpoint.id, clientEndpoint: endpoint }] as typeof joins)
        : joins.filter((j) => j.clientEndpointId !== endpoint.id);

    return true;
}

/** Every address on an email endpoint — the field holds a comma-separated list. */
export function endpointRecipients(endpoint: ClientEndpoint): string[] {
    return ((endpoint as { emailAddress?: string }).emailAddress ?? "")
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean);
}

/**
 * One row per address across a set of email endpoints. On a provisioned fleet every site's
 * endpoint carries the same list under the same name, so expanding per endpoint prints each
 * address once per site. Collapse to the address (case-insensitively, keeping the first-seen
 * spelling) with how widely it appears for the row's sub line.
 */
export function uniqueRecipients(
    endpoints: ClientEndpoint[],
): { address: string; endpointNames: string[]; endpointCount: number }[] {
    const byAddress = new Map<string, { address: string; endpointNames: string[]; endpointCount: number }>();
    for (const endpoint of endpoints) {
        const name = endpoint.endpointName ?? "Email endpoint";
        const seen = new Set<string>();
        for (const address of endpointRecipients(endpoint)) {
            const key = address.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            const row = byAddress.get(key) ?? { address, endpointNames: [], endpointCount: 0 };
            row.endpointCount += 1;
            if (!row.endpointNames.includes(name)) row.endpointNames.push(name);
            byAddress.set(key, row);
        }
    }
    return Array.from(byAddress.values());
}

export type NotificationSettings = Record<AlertCategory, { email: boolean; relays: RelaySelection }>;

export function notificationsFromDefaults(d: FleetDefaults): NotificationSettings {
    return {
        collision: { email: d.notifyEmailOnCollision, relays: d.collisionRelays },
        speeding: { email: d.notifyEmailOnSpeeding, relays: d.speedingRelays },
        left: { email: d.notifyEmailOnLeftPass, relays: d.leftPassRelays },
        right: { email: d.notifyEmailOnRightPass, relays: d.rightPassRelays },
    };
}

export function readNotificationSettings(
    triggersByCategory: Partial<Record<AlertCategory, EventTrigger>>,
    wiring: RelayWiringMap = DEFAULT_RELAY_WIRING,
): NotificationSettings {
    const read = (c: AlertCategory) => ({
        email: notifiesByEmail(triggersByCategory[c]),
        relays: relaySelectionOf(triggersByCategory[c], wiring),
    });
    return { collision: read("collision"), speeding: read("speeding"), left: read("left"), right: read("right") };
}

/**
 * Whether the fleet has anywhere to send an email, which decides if "email on" is comparable.
 *
 * "Email on" with no addresses is something no truck can match — there is no endpoint to attach —
 * so comparing it pinned an override on every unit that no reset could clear.
 */
export const fleetCanEmail = (d: FleetDefaults | undefined): boolean =>
    (d?.notificationEmailAddresses ?? "").trim().length > 0;

export function notificationDifferences(
    current: NotificationSettings,
    defaults: NotificationSettings,
    canEmail = true,
): AlertCategory[] {
    return (Object.keys(defaults) as AlertCategory[]).filter((c) => {
        const emailComparable = canEmail || !defaults[c].email;
        return (emailComparable && current[c].email !== defaults[c].email) || current[c].relays !== defaults[c].relays;
    });
}

export const notificationsMatch = (a: NotificationSettings, b: NotificationSettings, canEmail = true) =>
    notificationDifferences(a, b, canEmail).length === 0;
